import { StrictMode, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider as MUIThemeProvider, CssBaseline } from '@mui/material';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import ErrorBoundary from '../../shared/components/error/ErrorBoundary';
import { ApplicationContextProvider } from '../../providers/application_context';
import { UserContextProvider } from '../../providers/user_context';
import { ToastProvider } from '../../shared/context/ToastContext';
import { CustomerProvider } from '../../features/onboarding/context/CustomerContext';
import { store } from '../store';
import muiTheme from '../../styles/muiTheme';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders Component
 * 
 * Consolidates all application-level providers in a single component.
 * This improves maintainability and makes it easier to test components.
 * 
 * Provider Order (important):
 * 1. StrictMode - React strict mode checks
 * 2. ReduxProvider - State management
 * 3. BrowserRouter - Routing
 * 4. ApplicationContextProvider - App-level context
 * 5. UserContextProvider - Authentication context
 * 6. MUIThemeProvider - Material-UI theme
 * 7. StyledThemeProvider - Styled-components theme
 * 8. ErrorBoundary - Error handling
 * 9. ToastProvider - Toast notifications
 * 10. CustomerProvider - Feature-specific context
 */
export const AppProviders = ({ children }: AppProvidersProps) => {
  return (
    <StrictMode>
      <ReduxProvider store={store}>
        <BrowserRouter>
          <ApplicationContextProvider>
            <UserContextProvider value={{}}>
              <MUIThemeProvider theme={muiTheme}>
                <StyledThemeProvider theme={muiTheme}>
                  <CssBaseline />
                  <ErrorBoundary>
                    <ToastProvider>
                      <CustomerProvider>
                        {children}
                      </CustomerProvider>
                    </ToastProvider>
                  </ErrorBoundary>
                </StyledThemeProvider>
              </MUIThemeProvider>
            </UserContextProvider>
          </ApplicationContextProvider>
        </BrowserRouter>
      </ReduxProvider>
    </StrictMode>
  );
};
